import React, { createRef, useState } from "react";

import Papa from "papaparse";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { Box } from "@material-ui/core";

import { appActions } from "@ip-store/actions";
import { pluralizeText } from "@ip-utils";

import { Button } from "./index";

function FileReader({
  appActions,
  children,
  columns,
  multiple,
  type,
  onRead,
  ...props
}) {
  const inputRef = createRef();
  const [loading, setLoading] = useState(false);

  function handleChange(e) {
    const files = [...e.currentTarget.files];
    e.currentTarget.value = null;
    setLoading(true);

    const promises = files.map((file) => {
      if (type === "csv") {
        return readCsv(file);
      } else {
        return readContent(file);
      }
    });

    Promise.all(promises)
      .then((data) => {
        onRead(multiple ? data : data[0]);
      })
      .catch(() => null)
      .finally(() => setLoading(false));
  }

  function readContent(file) {
    return new Promise((resolve) => {
      const fileReader = new window.FileReader();
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.readAsText(file);
    });
  }

  function readCsv(file) {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        complete: ({ data, errors }) => {
          setLoading(false);
          if (errors.length) {
            errors.forEach((error) =>
              appActions.storeNotification("error", error.message)
            );
            reject(errors);
          } else {
            if (data.length && columns) {
              const keys = Object.keys(data[0]);
              if (!Array.isArray(columns)) {
                columns = columns.split(",");
              }

              const missing = [];
              const mappings = columns.map((column) => {
                const regExp = new RegExp(column, "i");
                const key = keys.find((key) => regExp.test(key));

                if (!key) {
                  missing.push(column);
                }

                return {
                  key,
                  column,
                };
              });

              if (missing.length) {
                let message = `Missing ${pluralizeText("column", missing)} `;
                if (missing.length > 1) {
                  message += `'${missing
                    .splice(0, missing.length - 1)
                    .join("', '")}' and `;
                }
                message += `'${missing[0]}'`;
                appActions.storeNotification("error", message);
              }

              data = data
                .map((item) => {
                  const line = {};
                  const mapped = mappings
                    .map((mapping) => {
                      const value = item[mapping.key];
                      line[mapping.column] = value;
                      return !!value;
                    })
                    .filter((key) => !!key);
                  return mapped.length ? line : null;
                })
                .filter((item) => !!item);
            }

            if (!data.length) {
              appActions.storeNotification("error", "No lines found");
            }

            resolve(data);
          }
        },
        header: true,
        skipEmptyLines: true,
      });
    });
  }

  return (
    <Box display="inline-block" position="relative">
      <Box display="none">
        <input
          accept={type ? `.${type}` : null}
          multiple={multiple}
          ref={inputRef}
          type="file"
          onChange={handleChange}
        />
      </Box>
      <Button
        loading={loading}
        outlined
        size="small"
        onClick={() => inputRef.current.click()}
        {...props}
      >
        {children || "File Reader"}
      </Button>
    </Box>
  );
}

const mapDispatchToProps = (dispatch) => ({
  appActions: bindActionCreators(appActions, dispatch),
});

export default connect(null, mapDispatchToProps)(FileReader);
